import { MutableRefObject } from 'react';
import { zeroAddress } from 'viem';
import { useQuery } from '@tanstack/react-query';
import type { Board, PaymentMethod, PaymentMethodGroup } from '../utils/Primitives';

export function getPaymentMethods(props: {boardRef: MutableRefObject<Board>}): PaymentMethodGroup {
    const { boardRef } = props;
    return boardRef.current.paymentMethodStore as PaymentMethodGroup;
}

export function getPaymentMethod(props: {
    boardRef: MutableRefObject<Board>,
    paymentMethodId: number;
}): PaymentMethod | null {
    const { boardRef, paymentMethodId } = props;
    const paymentStore = boardRef.current.paymentMethodStore as PaymentMethodGroup;
    return paymentStore[paymentMethodId] || null;
}

export const writePaymentMethodToBoard = (props: {boardRef: MutableRefObject<Board>, paymentMethod: PaymentMethod}) => {
    const { boardRef, paymentMethod } = props;
    const paymentStore = boardRef.current.paymentMethodStore as PaymentMethodGroup;
    paymentMethod.name = paymentMethod.name.trim();
    paymentMethod.symbol = paymentMethod.symbol.trim();
    paymentStore[paymentMethod.id] = paymentMethod;
}

export type useFetchSavePaymentMethodsReturn = {
    data: PaymentMethodGroup,
    isLoading: boolean,
    isFetched: boolean
}

export function useFetchSavePaymentMethods(props: {boardRef: MutableRefObject<Board>}): useFetchSavePaymentMethodsReturn {
    const { boardRef } = props;

    const { data, isLoading, isFetched } = useQuery({
        queryKey: ['paymentMethods'],
        queryFn: async () => {
            const ethPaymentMethod: PaymentMethod = {
                id: 0,
                address: zeroAddress,
                name: 'Ethereum',
                symbol: 'ETH',
                decimals: 18
            };

            const paymentMethods: PaymentMethodGroup = {
                [ethPaymentMethod.id]: ethPaymentMethod
            };
            writePaymentMethodToBoard({ boardRef, paymentMethod: ethPaymentMethod });

            const StickercloudApiUrl = process.env.NEXT_PUBLIC_STICKERCLOUD_API as string;
            if (!StickercloudApiUrl || StickercloudApiUrl.length === 0) {
                console.error(`Stickercloud API URL is not set`);
                return paymentMethods;
            }
            const paymentMethodsUrl = `${StickercloudApiUrl}payment-methods`;
            console.log(`Fetching payment methods from ${paymentMethodsUrl}`);

            const response = await fetch(paymentMethodsUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch payment methods: ${response.statusText}`);
            }

            const jsonFull = await response.json();
            if (!jsonFull['data'] || jsonFull['data'].length === 0) {
                return paymentMethods;
            }

            jsonFull['data'].forEach((method: any) => {
                const paymentMethod: PaymentMethod = {
                    id: Number(method['paymentMethodId']),
                    address: method['address'],
                    name: method['name'],
                    symbol: method['symbol'],
                    decimals: method['decimals']
                };
                paymentMethods[paymentMethod.id] = paymentMethod;
                writePaymentMethodToBoard({ boardRef, paymentMethod });
            });

            console.log(`Fetched and saved ${Object.keys(paymentMethods).length} payment methods`);
            return paymentMethods;
        },
    });

    return {
        data: data as PaymentMethodGroup,
        isLoading,
        isFetched
    };
}
